import React from "react";
import { Button } from "./ui/button";
import { Input } from "./ui/input";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "./ui/select";
import { Plus, Trash2, X } from "lucide-react";
import { Badge } from "./ui/badge";

export interface Filter {
    id: string;
    property: string;
    operator: string;
    value: string;
    valueTo?: string;
}

interface FormationFilterEditorProps {
    formationId: number;
    formationName: string;
    filters: Filter[];
    onUpdate: (filters: Filter[]) => void;
    onClose: () => void;
}

const properties = [
    { label: "Prénom", value: "first_name", type: "string" },
    { label: "Nom", value: "last_name", type: "string" },
    { label: "Email", value: "email", type: "string" },
    { label: "Status (Actif)", value: "is_active", type: "boolean" },
    { label: "Date d'inscription", value: "enrollment_date", type: "date" },
    { label: "Dernière activité", value: "last_activity_date", type: "date" },
];

const operatorsByProperty: Record<string, { label: string; value: string }[]> = {
    string: [
        { label: "Est égal à", value: "equals" },
        { label: "Contient", value: "contains" },
        { label: "Commence par", value: "starts_with" },
        { label: "Se termine par", value: "ends_with" },
    ],
    boolean: [
        { label: "Est", value: "is" },
    ],
    date: [
        { label: "Avant le début du cours", value: "before" },
        { label: "Après le début du cours", value: "after" },
        { label: "Le", value: "on" },
        { label: "Entre", value: "between" },
    ],
};

const FormationFilterEditor: React.FC<FormationFilterEditorProps> = ({
    formationName,
    filters,
    onUpdate,
    onClose
}) => {
    const addFilter = () => {
        const newFilter: Filter = {
            id: Math.random().toString(36).substr(2, 9),
            property: "enrollment_date",
            operator: "between",
            value: "",
        };
        onUpdate([...filters, newFilter]);
    };

    const updateFilter = (id: string, updates: Partial<Filter>) => {
        onUpdate(filters.map(f => f.id === id ? { ...f, ...updates } : f));
    };

    const removeFilter = (id: string) => {
        onUpdate(filters.filter(f => f.id !== id));
    };

    const getPropertyType = (propValue: string) => {
        return properties.find(p => p.value === propValue)?.type || "string";
    };

    return (
        <div className="bg-white border border-slate-200 rounded-2xl shadow-lg p-6 space-y-4 max-w-2xl w-full animate-in zoom-in-95 duration-200">
            <div className="flex justify-between items-center border-b pb-4">
                <div>
                    <h3 className="font-bold text-slate-900">Filtres pour : {formationName}</h3>
                    <p className="text-xs text-slate-500">Seuls les étudiants respectant tous ces critères seront sélectionnés.</p>
                </div>
                <Button variant="ghost" size="icon" onClick={onClose}>
                    <X className="h-4 w-4" />
                </Button>
            </div>

            <div className="space-y-3 min-h-[100px] max-h-[400px] overflow-y-auto pr-2">
                {filters.length === 0 ? (
                    <div className="flex flex-col items-center justify-center py-8 text-slate-400">
                        <p className="text-sm">Aucun filtre défini. Tous les étudiants seront sélectionnés.</p>
                    </div>
                ) : (
                    filters.map((filter) => {
                        const type = getPropertyType(filter.property);
                        return (
                            <div key={filter.id} className="flex gap-2 items-center bg-slate-50 p-3 rounded-xl border border-slate-100 group">
                                <Select
                                    value={filter.property}
                                    onValueChange={(val) => updateFilter(filter.id, { property: val, operator: operatorsByProperty[getPropertyType(val)][0].value })}
                                >
                                    <SelectTrigger className="w-[180px] bg-white text-xs">
                                        <SelectValue />
                                    </SelectTrigger>
                                    <SelectContent>
                                        {properties.map(p => <SelectItem key={p.value} value={p.value}>{p.label}</SelectItem>)}
                                    </SelectContent>
                                </Select>

                                <Select
                                    value={filter.operator}
                                    onValueChange={(val) => updateFilter(filter.id, { operator: val })}
                                >
                                    <SelectTrigger className="w-[140px] bg-white text-xs">
                                        <SelectValue />
                                    </SelectTrigger>
                                    <SelectContent>
                                        {operatorsByProperty[type].map(o => <SelectItem key={o.value} value={o.value}>{o.label}</SelectItem>)}
                                    </SelectContent>
                                </Select>

                                <div className="flex-1 flex gap-2">
                                    {type === 'boolean' ? (
                                        <Select
                                            value={filter.value}
                                            onValueChange={(val) => updateFilter(filter.id, { value: val })}
                                        >
                                            <SelectTrigger className="bg-white text-xs">
                                                <SelectValue placeholder="Choisir..." />
                                            </SelectTrigger>
                                            <SelectContent>
                                                <SelectItem value="true">Actif</SelectItem>
                                                <SelectItem value="false">Inactif</SelectItem>
                                            </SelectContent>
                                        </Select>
                                    ) : type === 'date' ? (
                                        <div className="flex gap-2 w-full">
                                            <Input
                                                type="date"
                                                value={filter.value}
                                                onChange={(e) => updateFilter(filter.id, { value: e.target.value })}
                                                className="bg-white text-xs h-10"
                                            />
                                            {filter.operator === 'between' && (
                                                <Input
                                                    type="date"
                                                    value={filter.valueTo}
                                                    onChange={(e) => updateFilter(filter.id, { valueTo: e.target.value })}
                                                    className="bg-white text-xs h-10"
                                                />
                                            )}
                                        </div>
                                    ) : (
                                        <Input
                                            placeholder="Valeur..."
                                            value={filter.value}
                                            onChange={(e) => updateFilter(filter.id, { value: e.target.value })}
                                            className="bg-white text-xs h-10"
                                        />
                                    )}
                                </div>

                                <Button
                                    variant="ghost"
                                    size="icon"
                                    className="h-8 w-8 text-slate-400 hover:text-red-500 opacity-0 group-hover:opacity-100 transition-opacity"
                                    onClick={() => removeFilter(filter.id)}
                                >
                                    <Trash2 className="h-4 w-4" />
                                </Button>
                            </div>
                        );
                    })
                )}
            </div>

            <div className="flex justify-between items-center pt-2">
                <Button
                    type="button"
                    variant="outline"
                    size="sm"
                    className="gap-2 text-xs font-bold"
                    onClick={addFilter}
                >
                    <Plus className="h-3 w-3" /> Ajouter un filtre
                </Button>
                <div className="flex items-center gap-2">
                    {filters.length > 0 && (
                        <Badge variant="secondary" className="bg-blue-50 text-blue-600 border-blue-100">
                            {filters.length} filtre{filters.length > 1 ? 's' : ''} actif{filters.length > 1 ? 's' : ''}
                        </Badge>
                    )}
                    <Button size="sm" className="font-bold text-xs" onClick={onClose}>
                        Terminer
                    </Button>
                </div>
            </div>
        </div>
    );
};

export default FormationFilterEditor;
